/**
 * Shared Firebase auth helpers for share link pages.
 * Used by both FolderSharePage and PlaylistSharePage.
 *
 * IMPORTANT: On share pages, auth.currentUser is unreliable after cross-tab logout.
 * Firebase Auth is global (shared via IndexedDB across tabs). When user logs out on
 * another tab, auth.currentUser can become null or switch to a different anonymous user
 * created by useShareAuthRecovery. The token cache (sessionStorage) ensures:
 *   1. Components can get a valid token when auth.currentUser is unstable (in-session)
 *   2. Page reload still works without re-entering password (cross-reload)
 *
 * Firebase anonymous users are NOT deleted server-side on signOut — only removed from
 * client IndexedDB. The UID + claims persist on Firebase's backend. So a cached token
 * (within its 1-hour TTL) remains valid for verifyIdToken even after the client lost
 * the user reference.
 */
import { auth, authInitialized } from '@/lib/firebase';
import {
  SHARE_TOKEN_KEY,
  decodeJwtPayload,
  isJwtNotExpired,
  isShareTokenValidForObjectId,
} from '@/lib/share-token';

/**
 * True when the cached token is stale relative to the current Firebase user. Stale means:
 *   0. Token is expired (JWT `exp` past) — Firebase ID token TTL is 1h, but cached
 *      tokens live in sessionStorage until the tab closes. A long-lived tab (>1h)
 *      would otherwise serve an expired token to backend on next call → 401 +
 *      confusing UX with no obvious recovery path.
 *   1. Cached is anonymous BUT a real (non-anonymous) user is now signed in (post-signIn
 *      from SignInSharePage or fresh login in another tab) — cached anon would resolve
 *      to no user on backend → 403 on SmallApp REST.
 *   2. Cached is from a DIFFERENT Firebase user than auth.currentUser. Common after
 *      signing out + signing in with a different account in the same tab (sessionStorage
 *      survives logout). On ACCOUNT-type shares the cached user's accountId mismatches
 *      and preAccessShareLink wrongly returns NOT_AUTHORIZED for the new (correct) user.
 * In any case, callers should refresh from `auth.currentUser` instead of using cache.
 */
export function isCachedTokenStale(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) return true;
  // Case 0: expired tokens are stale regardless of identity match. Backend
  // verifyIdToken would reject anyway — bail early so callers refetch.
  if (!isJwtNotExpired(payload)) return true;
  // Firebase ID tokens populate both `user_id` and `sub` with the user UID (string).
  // Type-guard against malformed/non-Firebase tokens that might encode them as
  // numbers — a numeric uid would never `=== currentUser.uid` (string), causing
  // valid cached tokens to be rejected as "stale" forever.
  const rawUid = payload.user_id ?? payload.sub;
  const cachedUid = typeof rawUid === 'string' ? rawUid : undefined;
  const currentUser = auth.currentUser;

  // Case 1: cached anon shadowed by a real user
  if (payload.firebase?.sign_in_provider === 'anonymous') {
    return !!currentUser && !currentUser.isAnonymous;
  }

  // Case 2: cached UID does not match the currently signed-in user.
  // Only check when there IS a current user — otherwise the cached token is the only
  // identity we have (cross-tab recovery scenario) and should be preserved.
  if (currentUser && cachedUid && cachedUid !== currentUser.uid) {
    return true;
  }

  return false;
}

/** Read cached token from sessionStorage. */
function _getCachedToken(): string | null {
  try {
    return sessionStorage.getItem(SHARE_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Write token to sessionStorage. */
function _setCachedToken(token: string): void {
  try {
    sessionStorage.setItem(SHARE_TOKEN_KEY, token);
  } catch {
    // sessionStorage full or disabled — in-memory fallback not needed,
    // worst case user re-enters password on reload
  }
}

/** Clear cached token (call on full re-auth or explicit logout). */
export function clearCachedShareToken(): void {
  try {
    sessionStorage.removeItem(SHARE_TOKEN_KEY);
  } catch {
    // ignore
  }
}

/** Explicitly cache a known-good token (call after postAccess + getIdToken(true)). */
export function cacheShareToken(token: string): void {
  _setCachedToken(token);
}

/**
 * Force-refresh the Firebase ID token after `preAccessShareLink` has updated the
 * server-side `shareLinkObjectIdTs` claim, then cache the fresh token in
 * sessionStorage. Use this from any code path that has just refreshed a share-
 * link claim and needs the JWT to carry the new timestamp.
 *
 * Why we cannot delegate to `getTokenWithClaim(objectId)` here:
 *   `getTokenWithClaim` returns the cached sessionStorage token when both
 *   `isCachedTokenStale` (Firebase `exp` 1h check) and `isShareTokenValidForObjectId`
 *   (claim's `shareLinkObjectId` value match) pass. Neither check inspects
 *   `shareLinkObjectIdTs` freshness — so a tab idle 30+ min would re-use a
 *   stale-claim token and skip the very Firebase refresh `preAccessShareLink`
 *   just made server-side. Backend `_augmentWithShareLinkContext` previously
 *   rejected stale claims via TTL guard → SmallApp REST proxy → augment skip
 *   → user's REAL accountId leaks into SmallApp microservice MongoDB save.
 *
 * Falls back to `getTokenWithClaim` when:
 *   - `auth.currentUser` is null (recovery scenarios)
 *   - `getIdToken(true)` throws (network failure mid-call)
 * Caller is expected to also pass `expectedObjectId` so the fallback retry
 * loop can verify claim presence on anonymous users where propagation may lag.
 *
 * Reference: claude-dev/2026-05-04-share-link-claim-stale/README.md
 */
export async function forceRefreshShareLinkToken(
  expectedObjectId?: string,
): Promise<string | null> {
  if (auth.currentUser) {
    try {
      const fresh = await auth.currentUser.getIdToken(true);
      _setCachedToken(fresh);
      return fresh;
    } catch {
      // network/Firebase error mid-call — fall through to retry-loop fallback
    }
  }
  return getTokenWithClaim(expectedObjectId);
}

/**
 * Get existing Firebase user's token WITHOUT creating anonymous user.
 * Use during init/preAccess phase where creating an anonymous session would
 * prevent the backend force-login check (doAccessShareLink skips force-login when token is present).
 *
 * Falls back to sessionStorage cached token when auth.currentUser is null. This handles:
 *   1. Race condition: SignInSharePage sets claims → navigates → FolderSharePage init
 *      runs → but auth.currentUser was destroyed by cross-tab Firebase IndexedDB sync.
 *   2. Page reload: auth.currentUser was replaced by a recovery anonymous user (no claims).
 *      The cached token from the original postAccess still has valid claims (within 1h TTL).
 */
export async function getExistingToken(): Promise<string | null> {
  await authInitialized;

  // Prefer cached token when available. After cross-tab logout, auth.currentUser
  // may be a recovery anonymous user (different UID, no claims) — its token would
  // fail backend _checkAuthenticated. The cached token is from the most recent
  // successful postAccess and is guaranteed to have the correct claims.
  // If cached token has expired (1h TTL), backend verifyIdToken will reject it,
  // preAccess returns NOT_AUTHORIZED → user re-enters password → new cache.
  // EXCEPTION: cached anonymous tokens are stale once the user has authenticated
  // as a real account member (ACCOUNT-type share flow) — fall through to fetch
  // the real user's token instead.
  const cached = _getCachedToken();
  if (cached && !isCachedTokenStale(cached)) return cached;

  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
}

/**
 * Retry getIdToken(true) until shareLinkObjectId claim propagates.
 * Firebase setCustomUserClaims propagation delay is usually <1s but can exceed 3s on beta
 * due to geographic latency between client and Firebase auth server.
 * Signed-in (non-anonymous) users don't need the claim — returns token immediately.
 *
 * Caches the last successful token to sessionStorage. Falls back to cached token when
 * auth.currentUser becomes null (cross-tab logout destroys Firebase auth via IndexedDB sync).
 *
 * @param expectedObjectId If provided, retries until claim MATCHES this objectId
 *                         (handles cross-link navigation where claim may be stale).
 */
export async function getTokenWithClaim(expectedObjectId?: string): Promise<string | null> {
  // Prefer cached token IF it already satisfies the claim requirement.
  // Without this check, a stale cached token (from a previous session OR from
  // SignInSharePage's pre-claim-propagation cache) would be returned without
  // forcing a refresh, causing backend NOT_FOUND_USER on write mutations.
  // EXCEPTION: cached anonymous tokens are stale once the user has authenticated
  // (ACCOUNT-type share flow) — refresh from auth.currentUser instead.
  const cached = _getCachedToken();
  if (
    cached &&
    isShareTokenValidForObjectId(cached, expectedObjectId) &&
    !isCachedTokenStale(cached)
  ) {
    return cached;
  }

  if (!auth.currentUser) {
    // No live user to refresh from. If we have any cached token, return it as best effort
    // (caller may still succeed on read operations even without exact claim match).
    return cached;
  }
  const MAX_ATTEMPTS = 8; // ~5.25s total vs 3s before
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const user = auth.currentUser;
    if (!user) return null;

    try {
      const token = await user.getIdToken(true);
      const payload = decodeJwtPayload(token);
      if (!payload) continue;
      // Both anonymous AND real cross-company users need shareLinkObjectId claim
      // for SmallApp REST `getMe()` proxy → GqlAuthGuard augmentation.
      // Backend sets claim on both via preAccessShareLink (after access verified).
      // If expectedObjectId provided, claim must match — handles cross-link
      // navigation (token may carry stale claim from a previous share visit).
      if (payload.shareLinkObjectId) {
        if (!expectedObjectId || payload.shareLinkObjectId === expectedObjectId) {
          _setCachedToken(token);
          return token;
        }
      }
      // Real users without an expectedObjectId requirement: backend may not set the
      // claim (rare — most share-page flows pass expectedObjectId). Accept token
      // as-is for them to avoid blocking generic getTokenString callers.
      if (!expectedObjectId && payload.firebase?.sign_in_provider !== 'anonymous') {
        _setCachedToken(token);
        return token;
      }
    } catch {
      // getIdToken failed (user destroyed) — retry
    }
    if (attempt < MAX_ATTEMPTS - 1) {
      await new Promise((r) => setTimeout(r, 750));
    }
  }
  // Retry exhausted — try one last fresh token. Only cache when the token actually
  // carries the expected `shareLinkObjectId` claim; otherwise we'd poison the cache
  // with a claim-less token that subsequent `getTokenString` calls would return
  // until TTL — sticking the user on persistent SmallApp REST 403 (per backend
  // GqlAuthGuard claim fallback). `isShareTokenValidForObjectId` is NOT enough on
  // its own because for non-anonymous users it returns true unconditionally
  // (line 58 share-token.ts) — that signature was designed for the original
  // header-based augmentation flow where real users didn't need a claim. With the
  // new claim-fallback flow (post `preAccessShareLink` setting claim on real users
  // too), real users DO need the claim for cross-company SmallApp REST.
  if (!auth.currentUser) return null;
  try {
    const token = await auth.currentUser.getIdToken(true);
    const payload = decodeJwtPayload(token);
    const claimMatches =
      !!payload?.shareLinkObjectId &&
      (!expectedObjectId || payload.shareLinkObjectId === expectedObjectId);
    if (claimMatches) {
      _setCachedToken(token);
    }
    return token;
  } catch {
    return null;
  }
}
